import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { Trans } from 'react-i18next';
import classNames from 'classnames';
import i18n from '../../i18n';

import CreditCardForm from './CreditCardForm';
import PaypalForm from './PaypalForm';
import PromoCodeForm from './PromoCodeForm';
import User from '../../models/User';
import { isCollectionPage } from '../../utils/url';
import { currenciesPaypalNotSupported } from '../currency';
import { Callout, Divider, Flex, Text, Link, Radio } from '@homeexchange/design';

import CreditCardDescription from './CreditCardDescription';
import PaymentMethodModel from '../../models/PaymentMethod';
import { checkBillingAddress } from '../../utils/Payment';
import { connect } from 'react-redux';
import TermsOfUseCheckbox from '../common/TermsOfUseCheckbox';

class PaymentDetails extends React.PureComponent {
    static propTypes = {
        context: PropTypes.oneOf(['messaging', 'subscription-payment', 'my-plan']),
        fields: PropTypes.element.isRequired,
        products: PropTypes.arrayOf(PropTypes.object),
        errorMessage: PropTypes.string,
        selectedPaymentMethod: PropTypes.string,
        onPaymentMethodChange: PropTypes.func,
        is3xPayment: PropTypes.bool,
        updateIs3xPayment: PropTypes.func,
        subscription: PropTypes.shape({
            // for 3x payment
            userHasSubscription: PropTypes.bool,
            autoRenew: PropTypes.bool,
            active: PropTypes.bool,
            currency: PropTypes.string
        }),
        is3xPaymentFromFinalization: PropTypes.bool,
        updateIs3xPaymentFromFinalization: PropTypes.func,
        needToBuyGp: PropTypes.bool,
        forwardedRef: PropTypes.object,
        isFunnelCollection: PropTypes.bool,
        getCouponName: PropTypes.func,
        hasHome: PropTypes.bool,
        user: PropTypes.instanceOf(User).isRequired,
        paymentMethods: PropTypes.arrayOf(PropTypes.instanceOf(PaymentMethodModel)),
        billingAddress: PropTypes.object,
        errorHandling: PropTypes.shape({
            errorMessage: PropTypes.string,
            formErrors: PropTypes.object,
            listFormErrors: PropTypes.func,
            setError: PropTypes.func,
            setFormError: PropTypes.func,
            handleError: PropTypes.func,
            handleApiError: PropTypes.func,
            handlePaypalError: PropTypes.func,
            clearFormErrors: PropTypes.func,
            scrollToFirstError: PropTypes.func
        }),
        showVerifyCardError: PropTypes.bool
    };

    disabledSubscriptionForm() {
        return (
            this.props.isFunnelCollection === undefined &&
            (this.props.hasHome === undefined || this.props.hasHome === false) &&
            isCollectionPage(window.location.href)
        );
    }

    render() {
        const {
            context,
            fields,
            errorMessage,
            selectedPaymentMethod,
            onPaymentMethodChange,
            forwardedRef,
            isFunnelCollection,
            paymentMethods,
            billingAddress,
            errorHandling,
            showVerifyCardError
        } = this.props;

        const termsOfUseLink = (
            <Link key="termsOfUseLink" external href={i18n.t('url:link_terms-of-use')} target="_blank">
                terms
            </Link>
        );

        const isValidBillingAddress = checkBillingAddress(billingAddress);

        const renderErrorCallout = (key, title, bodyHtml) => (
            <div className="payment-error-callout">
                <Callout key={key} hasIcon status="error">
                    <Text className="callout-title" isBold isSpan>
                        {title}
                    </Text>
                    <Text dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                </Callout>
            </div>
        );

        return (
            <div
                className={classNames({
                    'disabled-cursor-payment-form': this.disabledSubscriptionForm()
                })}
            >
                <div
                    className={`payment-details ${classNames({
                        'disabled-form': this.disabledSubscriptionForm(),
                        disabled: !isValidBillingAddress
                    })}`}
                    ref={forwardedRef}
                >
                    {showVerifyCardError &&
                        !errorMessage &&
                        renderErrorCallout(
                            'payment-verify-card-error',
                            i18n.t('subscription:payment-method.verifycard.error.callouttitle'),
                            i18n.t('subscription:payment-method.verifycard.error.calloutbody')
                        )}
                    {errorMessage &&
                        renderErrorCallout(
                            'payment-error',
                            'my-plan' === context
                                ? i18n.t('billing:payment-method.addmethod.error.calloutTitle')
                                : i18n.t('billing:error.title'),
                            errorMessage
                        )}

                    <Flex gap={'4'} direction="column" isFullWidth>
                        {!paymentMethods.length && 'my-plan' !== context && (
                            <section>
                                <Text size="xl" isBold>
                                    {i18n.t('billing:payment-method.choose.label')}
                                </Text>
                                <Text size="sm" color="secondary">
                                    {i18n.t('billing:payment-method.label.helper')}
                                </Text>
                            </section>
                        )}

                        <section className="p-relative">
                            <Radio
                                value={selectedPaymentMethod}
                                onChange={(value) => onPaymentMethodChange(value)}
                            >
                                <Radio.Item
                                    value="credit-card"
                                    id="credit-card"
                                    label={i18n.t('subscription:credit_card')}
                                />

                                {!currenciesPaypalNotSupported.includes(this.props.user.get('currency')) && (
                                    <Radio.Item
                                        value="paypal"
                                        id="paypal-payment-method"
                                        label={i18n.t('subscription:paypal')}
                                    />
                                )}
                            </Radio>
                            <CreditCardDescription />
                        </section>
                    </Flex>

                    <section
                        className={`stripe-section ${
                            selectedPaymentMethod !== 'credit-card' ? 'hidden' : ''
                        }`}
                    >
                        <CreditCardForm
                            fields={fields}
                            products={this.props.products}
                            is3xPayment={this.props.is3xPayment}
                            updateIs3xPayment={this.props.updateIs3xPayment}
                            subscription={this.props.subscription}
                            is3xPaymentFromFinalization={this.props.is3xPaymentFromFinalization}
                            updateIs3xPaymentFromFinalization={this.props.updateIs3xPaymentFromFinalization}
                            needToBuyGp={this.props.needToBuyGp}
                            isFunnelCollection={isFunnelCollection}
                        />
                    </section>

                    <section
                        className={`paypal-section ${selectedPaymentMethod !== 'paypal' ? 'hidden' : ''}`}
                    >
                        <PaypalForm />
                    </section>
                    {selectedPaymentMethod !== null && isFunnelCollection && (
                        <Flex gap="0" className="payment-form-code-promo-and-cgu">
                            <Divider space="small" />
                            <PromoCodeForm getCouponName={this.props.getCouponName} />
                            <Divider space="small" />
                            <TermsOfUseCheckbox errorHandling={errorHandling} />

                            <div className="form-group terms-of-use-checkbox">
                                <Text>
                                    <Trans
                                        i18nKey="collection:tou-approuval-collection_new"
                                        components={[termsOfUseLink]}
                                        i18n={i18n}
                                    />
                                </Text>
                            </div>
                        </Flex>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    billingAddress: state.billingAddress.billingAddress
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(
    forwardRef((props, ref) => <PaymentDetails {...props} forwardedRef={ref} />)
);
